import { ReactNode, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  ConfigProvider,
  Flex,
  Modal,
  Select,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, ShareAltOutlined } from "@ant-design/icons";
import {
  ModalHeader,
  modalBlockLabelStyle,
  modalButtonsTheme,
  modalSubtitleStyle,
} from "components/modals/ModalChrome";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import RemovePackageMemberModal from "components/modals/RemovePackageMemberModal";
import {
  personFilterOption,
  renderPersonOption,
} from "shared/components/PersonSelectOption";
import {
  useAddPackageMember,
  useRemovePackageMember,
} from "hooks/mutations/package-sharing";
import { getAllCustomerByBusinessId } from "hooks/queries/customer";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { CustomerPackage } from "shared/types";
import {
  packageSharingCapacity,
  translateMessage,
  ACCOUNT_STATUS_LABELS,
  ACCOUNT_STATUS_TONE,
  PACKAGE_OWNER_LABEL,
  sharingSlotsLabel,
} from "shared/utils";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

// One person with access. The initial avatar gives the list a left rail so
// names align and the row reads as a person rather than a line of text.
const PersonRow = ({
  name,
  badge,
  inactive = false,
  action,
  divider,
  onOpen,
}: {
  name: string;
  badge?: string;
  /** Deactivated at this studio: they keep their place, without access. */
  inactive?: boolean;
  action?: ReactNode;
  divider?: boolean;
  /** Opens this person's own client page. */
  onOpen?: () => void;
}) => (
  <Flex
    align="center"
    justify="space-between"
    gap={10}
    // A real button role rather than a bare onClick, so the row is reachable
    // and activatable from the keyboard like the rest of the app's row-nav.
    role={onOpen ? "button" : undefined}
    tabIndex={onOpen ? 0 : undefined}
    onClick={onOpen}
    onKeyDown={
      onOpen
        ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onOpen();
            }
          }
        : undefined
    }
    className={onOpen ? "sharing-person-row" : undefined}
    style={{
      padding: "11px 14px",
      cursor: onOpen ? "pointer" : "default",
      ...(divider ? { borderTop: `1px solid ${colors.line}` } : {}),
    }}
  >
    <Flex align="center" gap={10} style={{ minWidth: 0 }}>
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          background: colors.bgSoft,
          color: colors.ink2,
          fontSize: textPx(12),
          fontWeight: 600,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {name.trim().charAt(0).toUpperCase() || "?"}
      </span>
      <Text ellipsis style={{ fontWeight: 500 }}>
        {name}
      </Text>
      {badge && <StatusBadge tone="neutral" label={badge} dot={false} />}
      {inactive && (
        <StatusBadge tone={ACCOUNT_STATUS_TONE.inactive} label={ACCOUNT_STATUS_LABELS.inactive} />
      )}
    </Flex>
    {action}
  </Flex>
);

interface Props {
  customerPackage: CustomerPackage;
  disabled?: boolean;
  // The owner is deactivated at the studio: the package stays manageable for
  // its active co-sharers, but only the owner chooses who shares it, so
  // nobody new is added until they are reactivated.
  addDisabled?: boolean;
}

// Who, besides the buyer, may spend this package's credits.
//
// The owner is never listed as removable: ownership is the customer_package row
// itself, not a membership, so "removing" them is not a thing this modal can do.
const ManagePackageSharingModal = ({
  customerPackage,
  disabled,
  addDisabled = false,
}: Props) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | undefined>();
  const [confirming, setConfirming] = useState(false);

  const { mutate: addMember, isPending: adding } = useAddPackageMember();
  const { mutate: removeMember, isPending: removing } = useRemovePackageMember();
  // Only fetched while the modal is open; the client list is large and this is
  // a rarely-opened surface.
  const { data: customers } = getAllCustomerByBusinessId(
    open ? { is_active: true } : undefined,
  );

  const activeMembers = useMemo(
    () => (customerPackage.members ?? []).filter((m) => !m.deleted_at),
    [customerPackage.members],
  );
  // Capacity math lives in shared/utils so the package card's caption reads
  // the same "N of M" this badge does.
  const { used, max: maxMembers, isFull } = packageSharingCapacity(customerPackage);

  const options = useMemo(() => {
    const taken = new Set([
      customerPackage.customer_id,
      ...activeMembers.map((m) => m.user_id),
    ]);
    return (customers ?? [])
      .filter((c) => !taken.has(c.id))
      .map((c) => ({
        value: c.id,
        label: c.full_name,
        phone: c.phone_number,
      }));
  }, [customers, activeMembers, customerPackage.customer_id]);

  // Open a person's own client page, Paket tab. A normal push (not replace) is
  // what makes the browser Back button return to THIS package page, which is
  // the behaviour asked for. The modal is closed first so Back does not land on
  // a page with a stale modal still open over it.
  const openClient = (userId: string) => {
    setOpen(false);
    navigate(
      `${PATHS.CUSTOMER}/${userId}?tab=${
        customerPackage.package_deal.unlimited ? "4" : "2"
      }&sub=active`,
    );
  };

  const onAdd = () => {
    if (!selected) return;
    addMember(
      { customerPackageId: customerPackage.id, userId: selected },
      {
        onSuccess: () => setSelected(undefined),
        // The server owns every rule here (switch off, tier not shareable,
        // cap reached, client not at this studio, package expired), so its
        // message is the useful one rather than a generic failure.
        onError: (e: any) =>
          message.error(
            translateMessage(
              e?.response?.data?.error ??
                translate("messages.error.retrieve_data"),
            ),
          ),
      },
    );
  };

  // Hide this modal while the confirm is up, rather than stacking one modal on
  // another: stacked modals show two close buttons, dim the page twice, and
  // leave the list visible but unusable behind the confirm. One surface at a
  // time, then this one comes back.
  const [pendingRemoval, setPendingRemoval] = useState<{
    userId: string;
    name: string;
  } | null>(null);

  const onRemove = (userId: string, name: string) => {
    setConfirming(true);
    setPendingRemoval({ userId, name });
  };

  const closeRemoval = () => {
    setPendingRemoval(null);
    setConfirming(false);
  };

  return (
    <>
      <Button
        type="text"
        size="small"
        icon={<ShareAltOutlined />}
        disabled={disabled}
        onClick={() => setOpen(true)}
        aria-label={translate("package_sharing.manage_title")}
      />
      {pendingRemoval && (
        <RemovePackageMemberModal
          open
          memberName={pendingRemoval.name}
          memberUserId={pendingRemoval.userId}
          customerPackageId={customerPackage.id}
          packageName={customerPackage.package_deal.name}
          onConfirm={() => {
            removeMember({
              customerPackageId: customerPackage.id,
              userId: pendingRemoval.userId,
            });
            closeRemoval();
          }}
          onCancel={closeRemoval}
        />
      )}
      <ConfigProvider theme={modalButtonsTheme}>
        <Modal
          open={open && !confirming}
          onCancel={() => setOpen(false)}
          footer={null}
          destroyOnClose
          title={
            <ModalHeader
              title={translate("package_sharing.manage_title")}
              eyebrow={customerPackage.package_deal.name}
            />
          }
        >
          <Text style={modalSubtitleStyle}>
            {translate("package_sharing.manage_subtitle")}
          </Text>

          {/* Capacity as an eyebrow + count rather than a bare sentence, so the
              list below reads as "these are the N slots" at a glance. */}
          <Flex
            align="center"
            justify="space-between"
            style={{ marginTop: 22, marginBottom: 10 }}
          >
            <span style={modalBlockLabelStyle}>
              {translate("package_sharing.shared_with")}
            </span>
            {/* Green when every slot is taken: a full package is a completed
                state, not a problem. Grey read as "disabled/inactive", which
                is the wrong signal for a package doing exactly what it was
                sold to do. */}
            <StatusBadge
              tone={isFull ? "done" : "navy"}
              dot={false}
              label={sharingSlotsLabel(used, maxMembers)}
            />
          </Flex>

          {/* One bordered list instead of loose rows: the owner and each member
              are the same kind of thing (a person with access), so they share a
              container and are separated by hairlines rather than whitespace. */}
          <div
            style={{
              border: `1px solid ${colors.line}`,
              borderRadius: 12,
              overflow: "hidden",
              background: colors.card,
            }}
          >
            <PersonRow
              name={customerPackage.owner_name ?? "-"}
              badge={PACKAGE_OWNER_LABEL}
              inactive={customerPackage.owner_active === false}
              onOpen={() => openClient(customerPackage.customer_id)}
            />
            {activeMembers.map((m) => {
              const name = m.customer?.user?.full_name ?? m.full_name ?? "-";
              return (
                <PersonRow
                  key={m.user_id}
                  name={name}
                  divider
                  inactive={m.active === false}
                  onOpen={() => openClient(m.user_id)}
                  action={
                    <Button
                      type="text"
                      danger
                      size="small"
                      loading={removing}
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        // The row navigates; the trash must not.
                        e.stopPropagation();
                        onRemove(m.user_id, name);
                      }}
                      aria-label={translate("package_sharing.remove_client")}
                    />
                  }
                />
              );
            })}
            {activeMembers.length === 0 && (
              <div
                style={{
                  padding: "14px 14px",
                  borderTop: `1px solid ${colors.line}`,
                  background: colors.bgSoft,
                  fontSize: textPx(12.5),
                  color: colors.ink3,
                }}
              >
                {translate("package_sharing.no_members")}
              </div>
            )}
          </div>

          {/* Add row, or the reason there isn't one. Showing a live picker that
              cannot succeed is worse than replacing it with the explanation. */}
          {isFull ? (
            <div
              style={{
                marginTop: 14,
                padding: "10px 12px",
                borderRadius: 10,
                background: colors.bgSoft,
                fontSize: textPx(12.5),
                color: colors.ink2,
              }}
            >
              {translate("package_sharing.full")}
            </div>
          ) : (
            <Flex gap={8} style={{ marginTop: 14 }}>
              <Select
                style={{ flex: 1 }}
                disabled={addDisabled}
                value={selected}
                onChange={setSelected}
                placeholder={translate("package_sharing.select_client")}
                showSearch
                filterOption={personFilterOption}
                optionRender={renderPersonOption}
                options={options}
              />
              <Button
                type="primary"
                onClick={onAdd}
                loading={adding}
                disabled={!selected || addDisabled}
              >
                {translate("package_sharing.add_client")}
              </Button>
            </Flex>
          )}
        </Modal>
      </ConfigProvider>
    </>
  );
};

export default ManagePackageSharingModal;
